import { Button, Card, InputNumber, Message, Select, Space, Table } from "@arco-design/web-react";
import { Currency, Number } from "@/components/Numeric";
import { useCallback, useEffect, useState } from "react";
import { SOAPI, CustomerAPI } from "@/apis/sales";
import { ItemAPI } from "@/apis/material";
import { FileUtil, UserProfile, UserProfileType } from "@/utils";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import _ from "lodash";

export const LastSales = () => {
    const [customers, setCustomers] = useState([]);
    const [selectedCustomers, setSelectedCustomers] = useState([]);
    const [items, setItems] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [data, setData] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        total: 0,
    });
    const [orderBy, setOrderBy] = useState();
    const [orderDesc, setOrderDesc] = useState();
    const [days, setDays] = useState(30);

    const getCustomersAndItems = useCallback(async () => {
        const customers = await CustomerAPI.actives();
        const sortedCustomers = _.sortBy(customers, "customerName");
        setCustomers(sortedCustomers);
        const items = await ItemAPI.actives();
        const sortedItems = _.sortBy(items, "itemName");
        setItems(sortedItems);
    }, []);

    const getData = useCallback(async (page, customerIds, itemIds, orderBy, orderDesc) => {
        const result = await SOAPI.getLastSales({
            page,
            customerIds,
            itemIds,
            orderBy,
            orderDesc
        });
        setData(result.records);
        setPagination((pagination) => ({
            ...pagination,
            total: result.total,
        }));
    }, []);

    const saveProfile = () => {
        UserProfile.saveProfile(UserProfileType.LAST_SALES, {
            days
        });
    }

    useEffect(() => {
        getCustomersAndItems();
    }, [getCustomersAndItems]);

    useEffect(() => {
        const load = async () => {
            const profile = await UserProfile.getProfile(UserProfileType.LAST_SALES);
            if (profile) {
                profile.days && setDays(profile.days);
            }
        }
        load();
    }, []);

    useEffect(() => {
        getData(1, null, null, "soDate", true);
    }, [getData])

    return (
        <Card title="Last Sales"
            extra={<Space>
                <Select
                    mode="multiple"
                    showSearch
                    allowClear
                    placeholder="Select customers..."
                    maxTagCount={1}
                    style={{ width: 200 }}
                    value={selectedCustomers}
                    filterOption={(inputValue, option) =>
                        option.props.children.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
                    }
                    onChange={(value) => {
                        setSelectedCustomers(value);
                    }}
                    options={customers.map(item => ({
                        label: item.customerName,
                        value: item.id,
                    }))}
                />
                <Select
                    mode="multiple"
                    showSearch
                    allowClear
                    placeholder="Select items..."
                    maxTagCount={1}
                    style={{ width: 200 }}
                    value={selectedItems}
                    filterOption={(inputValue, option) =>
                        option.props.children.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
                    }
                    onChange={(value) => {
                        setSelectedItems(value);
                    }}
                    options={items.map(item => ({
                        label: item.itemName,
                        value: item.id
                    }))}
                />
                <Button type="primary" onClick={() => {
                    getData(1, selectedCustomers, selectedItems, orderBy, orderDesc);
                }}>Search</Button>
                <Button type="primary" onClick={async () => {
                    const res = await SOAPI.exportLastSales({
                        page: 1,
                        customerIds: selectedCustomers,
                        itemIds: selectedItems,
                        orderBy,
                        orderDesc
                    });
                    FileUtil.download(res.data, "last_sales.xlsx");
                }}>Export</Button>
                <Button type="primary" onClick={async () => {
                    await saveProfile();
                    Message.success("Profile Saved");
                }} hidden>Save Profile</Button>
            </Space>}>

            <Space>
                <span>Conditional Format: Color theme, if older than</span>
                <InputNumber
                    size="mini"
                    hideControl
                    style={{ width: 40 }}
                    value={days}
                    onChange={(value) => setDays(value)} />
                <span>days</span>
            </Space>
            <Table style={{ marginTop: 10 }} columns={[{
                    title: "SO#",
                    render: (col, record) => <Link to={`/workspace/sales/so/${record.soId}`}>{record.soNo}</Link>,
                    width: 110
                },
                {
                    title: 'Customer',
                    dataIndex: 'customerName',
                    sorter: true
                },
                {
                    title: 'Item Name',
                    dataIndex: 'itemName',
                    sorter: true
                },
                {
                    title: 'Last SO Date',
                    dataIndex: 'soDate',
                    sorter: true
                },
                {
                    title: 'Qty',
                    render: (col, record) => <Number value={record.quantity} />
                },
                {
                    title: 'Price',
                    render: (col, record) => <Currency value={record.rate} />
                },
                {
                    title: 'Amount',
                    render: (col, record) => <Currency value={record.amount} />
                },
            ]}
                hover={false}
                rowKey="id"
                rowClassName={(record) => {
                    return dayjs(record.soDate).isBefore(dayjs().subtract(days, 'day')) ? "highlight-tr" : "";
                }}
                scroll={{ y: 'calc(100vh - 340px)' }}
                data={data}
                pagination={pagination}
                onChange={(val, sorter) => {
                    setPagination(val);
                    setOrderBy(sorter.field);
                    setOrderDesc(sorter.direction === "descend");
                    getData(val.current, selectedCustomers, selectedItems, sorter.field, sorter.direction === "descend");
                }} />
        </Card>
    );
}